import { getErrorMsg } from 'app/core/errors/error-utils';
import { RootState } from 'app/core/redux/configure-store';
import { Opt } from 'app/tools/general.types';
import {
  useEffectOnce,
  useEffectOnChanges,
  useObservableSend,
  useWatchAsRef,
} from 'app/tools/hooks/misc-hooks';
import _ from 'lodash';
import { useObservableState } from 'observable-hooks';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  finalize,
  mergeMapTo,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { ApiError, isKeyedAxiosError } from '../api-core.types';
import { ApiSlice } from './api-slice-factory';

type RequestOpts = {
  fresh?: boolean;
  dontAutoFetch?: boolean;
};

type ItemHook<T, D> = (
  data: D,
  opts?: RequestOpts
) => {
  resource: T;
  request: () => void;
  cancel: () => void;
  loading: boolean;
  loadingMissing: boolean;
  error: Opt<ApiError>;
};

type ApiState = RootState['api'];
type Names = keyof ApiState;
type Value<N extends Names> = ApiState[N]['value'];

const apiItemHookFactory = <N extends Names, D = void>(
  slice: ApiSlice<Value<N>, N>,
  source$: (data: D) => Observable<Value<N>>
): ItemHook<Value<N>, D> => {
  const { actions, name } = slice;
  let lastData: Opt<D>;

  const useResource = (data: D, opts?: RequestOpts) => {
    const dispatch = useDispatch();

    const apiStore = useSelector(state => state.api[name]) || {};
    const appOnline = useSelector(s => s.context.appOnline);
    const refetch = appOnline === 'backOnline';

    const valueRef = useWatchAsRef(apiStore.value);
    const cacheReq =
      !opts?.fresh && valueRef.current && _.isEqual(data, lastData);
    const autoFetch = !cacheReq && !opts?.dontAutoFetch;

    // NOTE: until initialised, loading = true, value = undefined, err = undefined.
    // this means when loading from fresh, loading never starts out false,
    // and old errors and values never show even for a split second.
    const [loadInitialised, setLoadInitialised] = useState(
      !autoFetch
    );
    const [valInitialised, setValInitialised] = useState(cacheReq);
    const [errInitialised, setErrInitialised] = useState(false);

    useEffect(() => {
      if (apiStore.value === undefined && !valInitialised) {
        setValInitialised(true);
      }
      if (apiStore.error === undefined && !errInitialised) {
        setErrInitialised(true);
      }
    }, [
      apiStore.error,
      apiStore.value,
      errInitialised,
      valInitialised,
    ]);

    useEffectOnce(() => {
      if (!cacheReq) {
        // NOTE: only clear old storage if different data parameter is passed
        dispatch(actions.set(undefined));
        lastData = data;
      }
    });

    const resource = valInitialised ? apiStore.value : undefined;
    const loading = loadInitialised ? !!apiStore.loading : true;
    const error = errInitialised ? apiStore.error : undefined;

    const [cancel, cancel$] = useObservableSend();

    const request$ = of(data).pipe(
      tap(() => dispatch(actions.start())),
      tap(() => dispatch(actions.clearErrors())),
      tap(() => {
        if (!loadInitialised) setLoadInitialised(true);
      }),
      switchMap(d => source$(d).pipe(takeUntil(cancel$))),
      tap(v => dispatch(actions.success<Value<N>>(v))),
      catchError((e: unknown) => {
        const errorRtn = isKeyedAxiosError(e)
          ? e.data
          : ({ detail: getErrorMsg(e) } as ApiError);

        dispatch(actions.failure(errorRtn));
        // failure -> okay to leave last fetch result in redux
        return EMPTY;
      }),
      finalize(() => dispatch(actions.end()))
    );

    const [, request] = useObservableState<Value<N>, void>(
      requestEvents$ =>
        requestEvents$.pipe(
          // mergeMap = allow multiple requests to interleave
          mergeMapTo(request$)
        )
    );

    useEffect(() => {
      // NOTE: should only run once, when init first equals true
      if (valInitialised && autoFetch) request();
    }, [autoFetch, valInitialised, request]);

    useEffectOnChanges(() => {
      if (refetch) request();
    }, [data, refetch, request]);

    return useMemo(
      () => ({
        resource,
        request,
        cancel,
        loading,
        loadingMissing: loading && !resource,
        error,
      }),
      [cancel, error, loading, request, resource]
    );
  };

  return useResource;
};

export default apiItemHookFactory;
